import { useCallback, useEffect, useState } from 'react';
import {
  getMerchants,
  getRecentTransactions,
  getRejectedTransactions,
  getTransactionHistory,
  getTransactionHistoryCount,
  getTransactionTotals,
  getTransactionTps,
} from '../api/client';

// Shortened from the original 15s (Dashboard.jsx's "Progress" page
// convention) to 3s (2026-08-19, explicit request for the live feed to
// track incoming transactions "in real-time"). Still plain polling, not a
// push/WebSocket connection - see the user's own answer when asked to
// choose between the two: faster polling was preferred over standing up
// new push infrastructure (API Gateway WebSocket + broadcaster) for this.
const POLL_INTERVAL_MS = 3000;

// Live summary's feed is capped to the most recent N rows after merging
// all three outcomes and sorting - 2026-08-19 explicit request ("only
// display 15 records"). The History tab is unaffected; it has its own
// 25-per-page/"Load more" pagination and isn't this constant.
const LIVE_FEED_LIMIT = 15;

// Rows per page in the History tab's client-side pagination (2026-08-26).
// Matches the API's own page size, so on a default search page 1 is exactly
// what came back in the first request and "Load more" adds one more page.
const HISTORY_PAGE_SIZE = 25;

// The History tab has TWO status filters, because the data has two
// independent axes (2026-08-26, user's explicit correction):
//
//   Callback delivery status - did THIS service get the notification to the
//                              merchant? success / failed (retrying) /
//                              exhausted.
//   Actual transaction status - did the transfer itself go through, or come
//                              back with a rejection code (DS24 and
//                              similar)?
//
// The combination that makes the distinction necessary: a rejected
// transaction is a SUCCESSFUL delivery. status=success, merchant returned
// 200, rejection_code=DS24. Nothing failed on our side; the money did not
// move. This was briefly built as one combined dropdown, which conflated
// the two and read as if `success` meant the transfer completed.
const TXN_STATUS_ANY = '';
const TXN_STATUS_REJECTED = 'rejected';
const TXN_STATUS_OK = 'ok';

// Back down to 15 from 500 (2026-08-26). Both Live summary tables are now
// previews with a "View all today" link into the History tab, which is
// DynamoDB-backed, paginated and searchable - so this list no longer has to
// be the complete record, and the 3s poll no longer carries up to 500 rows
// with it. Matched to LIVE_FEED_LIMIT so the two tables above and below
// each other are the same length.
const REJECTED_TABLE_LIMIT = LIVE_FEED_LIMIT;

// The two transaction_type values this page cares about, per the user's
// own naming: QP2M = deposit, ICRT = withdrawal. Kept as one place so the
// summary cards and the live feed's type badges/filter can't drift apart.
const DEPOSIT_TYPE = 'QP2M';
const WITHDRAWAL_TYPE = 'ICRT';

const currencyFormatter = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
});

function formatAmount(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return currencyFormatter.format(Number(value));
}

// Plain thousands-separated integer formatter (2026-08-20) - for the
// transaction-COUNT figures (AmountCard's "N transactions" line,
// TotalCountCard's headline number), as distinct from formatAmount()'s
// currency formatter above. No currency symbol/decimals, just the
// separator, so "2869" reads as "2,869" instead of squinting to count
// digits.
const countFormatter = new Intl.NumberFormat('en-US');

function formatCount(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '0';
  }
  return countFormatter.format(Number(value));
}

// Merchant ID -> merchant name, for the transaction tables' "Merchant"
// column (2026-08-24, user request: show the name instead of the raw ID).
//
// Resolved in the browser rather than server-side, deliberately. The
// merchant directory already comes back as one un-paginated list
// (P2MDashboard::merchants() - the Merchants page filters it client-side
// for the same reason), it changes on a scale of days while the live feed
// refreshes every 3s, and joining it into recentTransactions() would mean
// a Redis read of the whole directory on every poll. Fetching it once per
// page load and keeping a lookup in memory costs one request instead.
//
// Shared by both tabs via a hook rather than lifted to a context: the two
// tabs mount independently and each wants its own copy anyway, and one
// extra request on tab switch is cheaper than the wiring.
function useMerchantNames() {
  const [names, setNames] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getMerchants();
        if (cancelled) return;

        const map = {};
        for (const m of data.merchants || []) {
          if (m?.merchant_id) {
            map[m.merchant_id] = m.merchant_name || null;
          }
        }
        setNames(map);
      } catch {
        // Deliberately silent. A failed directory lookup degrades the
        // Merchant column to raw IDs (see merchantLabel below), which is
        // exactly what this page showed before - not worth an error banner
        // on a page whose actual job is the transaction feed.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return names;
}

// Name when known, raw ID when not. The fallback matters more than it
// looks: the directory is a separate Redis key this repo doesn't write, so
// a merchant can legitimately appear in the feed before it appears there -
// showing a dash would make a real transaction look broken.
function merchantLabel(names, merchantId) {
  if (!merchantId) {
    return '—';
  }
  return names[merchantId] || merchantId;
}

// "View all" link shared by both Live summary tables (2026-08-26). Renders
// as a link but is a <button>: it changes tab state rather than navigating,
// and a real <a href> here would be a lie to keyboard and screen-reader
// users about what happens on activation.
function ViewAllLink({ onClick, children }) {
  return (
    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={onClick}>
      {children} &rarr;
    </button>
  );
}

function typeBadge(transactionType) {
  if (transactionType === DEPOSIT_TYPE) {
    return <span className="badge text-bg-success">Deposit (QP2M)</span>;
  }
  if (transactionType === WITHDRAWAL_TYPE) {
    return <span className="badge text-bg-primary">Withdrawal (ICRT)</span>;
  }
  return <span className="badge text-bg-secondary">{transactionType || '—'}</span>;
}

function statusBadge(status) {
  const variant = { success: 'text-bg-success', failed: 'text-bg-warning', exhausted: 'text-bg-danger' }[status]
    || 'text-bg-secondary';
  return <span className={`badge ${variant} text-capitalize`}>{status || '—'}</span>;
}

// The ACTUAL transaction outcome, which is a different axis from
// statusBadge() above (that one is callback DELIVERY). A rejected
// transaction is a successful delivery carrying a rejection code, so the
// two columns sitting side by side will legitimately read
// "Success / Rejected" - that is the distinction this page exists to make
// visible, not a contradiction.
//
// Named "Paid" rather than "Success" deliberately (user's wording): with
// "Success" in the delivery column right beside it, two identical words
// meaning different things is exactly the confusion to avoid.
function transactionStatusBadge(rejectionCode) {
  const code = typeof rejectionCode === 'string' ? rejectionCode.trim() : '';

  if (code !== '') {
    return (
      <span className="badge text-bg-danger" title={`Rejected — ${code}`}>
        Rejected
      </span>
    );
  }

  return <span className="badge text-bg-success">Paid</span>;
}

// Real-time (15s-polled) summary + live feed, and an on-demand, DynamoDB-
// backed "History" tab for arbitrary date-range/filtered transaction search
// (2026-08-14 - see TransactionHistoryStore/P2MDashboard::transactionHistory()
// on the Lumen side). Tabbed on one page rather than two separate nav items,
// since both views are about the same underlying concept ("transactions"),
// just different time windows/scopes.
export default function TransactionMonitoring() {
  const [activeTab, setActiveTab] = useState('summary');

  // Set by Live summary's "View all" links, consumed once by HistoryTab as
  // its starting filters (2026-08-26). Lives here because the two tabs are
  // siblings and this is the only state they share. An incrementing `token`
  // rather than a plain object so clicking the same link twice still
  // re-triggers the search - React bails out of a state update when the
  // value is identical, and a repeat click is a legitimate "take me back
  // there" action.
  const [historyPreset, setHistoryPreset] = useState(null);

  const viewAll = (preset) => {
    setHistoryPreset({ ...preset, token: Date.now() });
    setActiveTab('history');
  };

  return (
    <div>
      <h1 className="h4 mb-3">Transaction monitoring</h1>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link${activeTab === 'summary' ? ' active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Live summary <span className="live-pulse-dot" aria-hidden="true" />
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link${activeTab === 'history' ? ' active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </li>
      </ul>

      {activeTab === 'summary' ? <SummaryTab onViewAll={viewAll} /> : <HistoryTab preset={historyPreset} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live summary tab - today's totals (polled every 15s) + a recent-successful
// feed. Both reflect successfully-completed transactions only - see
// P2MDashboard::transactionTotals()'s docblock.
// ---------------------------------------------------------------------------
function SummaryTab({ onViewAll }) {
  const merchantNames = useMerchantNames();

  const [totals, setTotals] = useState(null);
  const [totalsError, setTotalsError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [feed, setFeed] = useState([]);
  const [feedError, setFeedError] = useState(null);

  const [tps, setTps] = useState(null);
  const [tpsError, setTpsError] = useState(null);

  const load = useCallback(async () => {
    const [totalsResult, feedResult, tpsResult] = await Promise.allSettled([
      getTransactionTotals(),
      // Requests all three outcomes (success/failed/exhausted - see
      // P2MDashboard::recentTransactions()'s response shape), unlike the
      // totals cards above which stay success-only. 25 per outcome, same
      // per_page convention used elsewhere on this dashboard (e.g.
      // getExhaustedCallbacks()'s default) - merged/sorted below rather than
      // requesting one huge combined list, since the backend already keeps
      // these as three separate bounded Redis sorted sets.
      getRecentTransactions(25),
      // Live TPS card (2026-08-19) - see P2MDashboard::transactionTps()'s
      // docblock. Fetched in the same Promise.allSettled batch as the two
      // above so all three cards refresh together on the same 3s tick,
      // rather than drifting out of sync with their own independent timers.
      getTransactionTps(),
    ]);

    if (totalsResult.status === 'fulfilled') {
      setTotals(totalsResult.value);
      setTotalsError(null);
    } else {
      setTotalsError(totalsResult.reason?.message || 'Failed to load transaction totals.');
    }

    if (tpsResult.status === 'fulfilled') {
      setTps(tpsResult.value);
      setTpsError(null);
    } else {
      setTpsError(tpsResult.reason?.message || 'Failed to load live TPS.');
    }

    if (feedResult.status === 'fulfilled') {
      const { success = [], failed = [], exhausted = [] } = feedResult.value || {};
      const merged = [...success, ...failed, ...exhausted]
        .filter((row) => row.transaction_type === DEPOSIT_TYPE || row.transaction_type === WITHDRAWAL_TYPE)
        // created_at is "Y-m-d H:i:s.u" (SendNotification's own format) -
        // zero-padded, so a plain string comparison already sorts
        // chronologically without needing to parse it into a Date first.
        // Missing created_at sorts last rather than throwing/NaN-ing.
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        .slice(0, LIVE_FEED_LIMIT);
      setFeed(merged);
      setFeedError(null);
    } else {
      setFeedError(feedResult.reason?.message || 'Failed to load recent transactions.');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted py-5">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        Loading transaction monitoring…
      </div>
    );
  }

  return (
    <div>
      {totalsError && <div className="alert alert-danger py-2">{totalsError}</div>}

      {tpsError && <div className="alert alert-danger py-2">{tpsError}</div>}

      {totals && (
        <>
          <div className="row g-3 mb-2">
            <AmountCard
              label="Total deposits today (QP2M)"
              amount={totals.deposit?.amount}
              count={totals.deposit?.count}
              rejectedAmount={totals.deposit?.rejected_amount}
              rejectedCount={totals.deposit?.rejected_count}
              color="#198754"
            />
            <AmountCard
              label="Total withdrawals today (ICRT)"
              amount={totals.withdrawal?.amount}
              count={totals.withdrawal?.count}
              rejectedAmount={totals.withdrawal?.rejected_amount}
              rejectedCount={totals.withdrawal?.rejected_count}
              color="#0d6efd"
            />
            <TotalCountCard
              count={(totals.deposit?.attempts ?? 0) + (totals.withdrawal?.attempts ?? 0)}
              rejectedCount={(totals.deposit?.rejected_count ?? 0) + (totals.withdrawal?.rejected_count ?? 0)}
              color="#6f42c1"
            />
            <TpsCard tps={tps?.tps} windowSeconds={tps?.window_seconds} today={tps?.today} color="#ffba00" />
          </div>
          <p className="text-muted small mb-4">
            For {totals.date} · Last refreshed: {totals.generated_at}
          </p>
        </>
      )}

      <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
        <h2 className="h6 text-uppercase text-muted mb-0">Recent transactions (last {LIVE_FEED_LIMIT})</h2>
        <ViewAllLink onClick={() => onViewAll({ from: todayIso(), to: todayIso(), status: 'success' })}>
          View all today
        </ViewAllLink>
      </div>

      {feedError && <div className="alert alert-danger py-2">{feedError}</div>}

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Type</th>
                <th>Callback delivery</th>
                {/* The transaction's OWN outcome, next to the delivery
                    outcome on purpose: this feed carries both paid and
                    rejected rows (a rejection is a successful delivery),
                    and without this column they were indistinguishable
                    here. Added 2026-08-27 on request. */}
                <th>Transaction status</th>
                <th>Instruction ID</th>
                <th>Merchant order ID</th>
                <th>Merchant</th>
                <th className="text-end">Amount</th>
                <th>Created at</th>
              </tr>
            </thead>
            <tbody>
              {feed.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted text-center py-3">
                    No recent QP2M/ICRT transactions.
                  </td>
                </tr>
              )}
              {feed.map((row) => (
                <tr key={row.merchant_order_id}>
                  <td>{typeBadge(row.transaction_type)}</td>
                  <td>{statusBadge(row.status)}</td>
                  <td>{transactionStatusBadge(row.rejection_code)}</td>
                  <td className="text-nowrap">{row.instruction_id || '—'}</td>
                  <td className="text-break">{row.merchant_order_id}</td>
                  {/* title, not a second line: keeps the row height and the
                      raw ID one hover away for a lookup or a History filter. */}
                  <td className="text-break" title={row.merchant_id || ''}>
                    {merchantLabel(merchantNames, row.merchant_id)}
                  </td>
                  <td className="text-end">{formatAmount(row.amount)}</td>
                  <td>{row.created_at || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <RejectedTable merchantNames={merchantNames} onViewAll={onViewAll} />
    </div>
  );
}

// Rejected transactions for today (2026-08-24, user request), sitting under
// the live feed. "Rejected" = the callback was delivered fine but the
// payload carried a non-empty rejection_code; DS24 (Bacnet unreachable or
// silent for 20s) is the usual one. These are the transactions whose
// amounts are excluded from the peso cards above, so this table is the
// audit trail for that exclusion.
//
// Polled on the same 3s cadence as everything else on this tab, but as its
// OWN request rather than being folded into the feed: it is a different
// day-scoped query with its own cap, and keeping it separate means a slow
// or failed rejected-list read never delays or blanks the live feed.
function RejectedTable({ merchantNames, onViewAll }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const result = await getRejectedTransactions({ limit: REJECTED_TABLE_LIMIT });
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load rejected transactions.');
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  const rows = data?.transactions || [];
  const totalCount = data?.total_count ?? 0;

  // total_count comes from a counter, rows.length from a capped list - when
  // they disagree the table is showing a slice, and says so rather than
  // implying these are all of them.
  const truncated = totalCount > rows.length;

  return (
    <div className="mt-4">
      <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
        {/* Heading and link grouped so justify-content-between puts the
            pair on the left and the summary on the right - three loose
            children would strand the link in the middle, and it would not
            line up with the same link on the table above. */}
        <div className="d-flex align-items-baseline gap-3">
          <h2 className="h6 text-uppercase text-muted mb-0">
            Rejected transactions today (last {REJECTED_TABLE_LIMIT})
          </h2>
          <ViewAllLink onClick={() => onViewAll({ from: todayIso(), to: todayIso(), rejected: true })}>
            View all today
          </ViewAllLink>
        </div>
        {data && (
          <div className="small text-muted">
            {formatCount(totalCount)} rejected &middot; {formatAmount(data.total_amount)} excluded from totals
            {/* This table is a preview now, so "showing newest N" is the
                normal state rather than a trim warning - the full list is
                one click away in History. */}
            {truncated && ` · showing newest ${formatCount(rows.length)}`}
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Type</th>
                <th>Rejection code</th>
                <th>Instruction ID</th>
                <th>Merchant order ID</th>
                <th>Merchant</th>
                <th className="text-end">Amount</th>
                <th>Created at</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted text-center py-3">
                    {data ? 'No rejected transactions today.' : 'Loading…'}
                  </td>
                </tr>
              )}
              {rows.map((row, i) => (
                // Rows are snapshots from a Redis ZSET, not live records, so
                // merchant_order_id is the natural key - the index is only a
                // fallback for a malformed snapshot.
                // eslint-disable-next-line react/no-array-index-key
                <tr key={row.merchant_order_id || i}>
                  <td>{typeBadge(row.transaction_type)}</td>
                  <td>
                    <span className="badge text-bg-danger">{row.rejection_code || '—'}</span>
                  </td>
                  <td className="text-nowrap">{row.instruction_id || '—'}</td>
                  <td className="text-break">{row.merchant_order_id || '—'}</td>
                  <td className="text-break" title={row.merchant_id || ''}>
                    {merchantLabel(merchantNames, row.merchant_id)}
                  </td>
                  <td className="text-end">{formatAmount(row.amount)}</td>
                  <td>{row.created_at || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AmountCard({ label, amount, count, rejectedAmount, rejectedCount, color }) {
  return (
    <div className="col-12 col-sm-6 col-lg-3">
      {/* NOT `border-0` (fixed 2026-08-24). Bootstrap defines that utility
          as `border: 0 !important`, and an !important stylesheet rule beats
          a non-important inline style - so the coloured left edge these
          cards have carried since they were written never actually
          rendered. A transparent 1px border reproduces the borderless look
          exactly while letting the inline borderLeft through, which matters
          now that red is what distinguishes a rejected card from the total
          it was excluded from. */}
      <div
        className="card h-100 shadow-sm"
        style={{ border: '1px solid transparent', borderLeft: `4px solid ${color}` }}
      >
        <div className="card-body">
          <div className="text-muted small">{label}</div>

          <div className="fs-3 fw-bold mt-1">{formatAmount(amount)}</div>

          {/* Its own line under the total, in small type (2026-08-24, user's
              call after seeing it inline). Side by side, an eight-figure
              peso value plus the rejected pair overflows a quarter-width
              card and flex-wrap drops the second half onto a new line
              anyway - behind a dangling separator. Stacking it deliberately
              gets the same two-line result, predictably, at every width and
              with no separator to strand.

              Red and small: visible without competing with the headline,
              which is the number the page exists to show. Always rendered,
              including at zero, so the card keeps a fixed height instead of
              growing and shrinking as rejections come and go. */}
          <div
            className="text-danger small"
            title={`${formatCount(rejectedCount)} rejected transaction${Number(rejectedCount) === 1 ? '' : 's'} - no money moved, so this amount is NOT included in the total above. They ARE included in the transaction count below.`}
          >
            <span className="fw-semibold">{formatAmount(rejectedAmount)}</span> (Rejected)
          </div>

          <div className="text-muted small mt-1">{formatCount(count)} transaction{count === 1 ? '' : 's'}</div>
        </div>
      </div>
    </div>
  );
}

// Combined deposit + withdrawal ATTEMPT count, today (2026-08-19) - a quick
// "how much total activity" headline sitting next to the two per-type
// AmountCards above and the Live TPS card below. Sums
// totals.deposit.attempts + totals.withdrawal.attempts (both already
// fetched by getTransactionTotals() for the two cards next to it) rather
// than a new API call. Deliberately reads `attempts`, NOT `count` -
// `count` is success-only (matches the `amount` sum on those two cards),
// but this card was explicitly asked to include failed attempts too, so it
// uses the any-outcome field instead (backed by
// CallbackStatsStore::recordAttempt() on the Lumen side - see
// P2MDashboard::transactionTotals()'s docblock). That means this card can
// legitimately be a larger number than deposit.count + withdrawal.count
// added together - that's the point, not a bug.
function TotalCountCard({ count, rejectedCount, color }) {
  return (
    <div className="col-12 col-sm-6 col-lg-3">
      {/* Not `border-0` - see AmountCard's note on why that utility's
          !important kills the inline colour bar. */}
      <div
        className="card h-100 shadow-sm"
        style={{ border: '1px solid transparent', borderLeft: `4px solid ${color}` }}
      >
        <div className="card-body">
          <div className="text-muted small">Total transactions today</div>
          <div className="fs-3 fw-bold mt-1">{formatCount(count)}</div>

          {/* Same small-red treatment as the rejected amount on the money
              cards, so "red and small underneath" reads as one convention
              across the row rather than three unrelated decorations.
              Deliberately a COUNT here, not a peso figure - this card's
              headline is a count, and mixing a currency value underneath it
              would invite reading the two as related when they measure
              different things. */}
          <div
            className="text-danger small"
            title="Transactions that completed with a rejection code (DS24 and similar). Unlike the peso totals, this card's headline COUNT still includes them - this line says how many of them there were."
          >
            <span className="fw-semibold">{formatCount(rejectedCount)}</span> (Rejected)
          </div>
        </div>
      </div>
    </div>
  );
}

// Rolling-average transactions-per-second (2026-08-19) - see
// P2MDashboard::transactionTps()'s docblock for the QP2M/ICRT-any-outcome
// scope and the 10s window this averages over. Same card shell as
// AmountCard above (same col width, same left-accent-bar treatment) so the
// three cards read as one row of equally-weighted stats, just with a rate
// instead of a currency amount as the headline figure.
function TpsCard({ tps, windowSeconds, today, color }) {
  const display = tps === null || tps === undefined || Number.isNaN(Number(tps))
    ? '—'
    : Number(tps).toFixed(2);

  return (
    <div className="col-12 col-sm-6 col-lg-3">
      {/* Not `border-0` - see AmountCard's note on why that utility's
          !important kills the inline colour bar. */}
      <div
        className="card h-100 shadow-sm"
        style={{ border: '1px solid transparent', borderLeft: `4px solid ${color}` }}
      >
        <div className="card-body">
          <div className="text-muted small">
            Live TPS <span className="live-pulse-dot" aria-hidden="true" />
          </div>
          <div className="fs-3 fw-bold mt-1">{display} <span className="fs-6 fw-normal text-muted">tx/s</span></div>
          <TpsTodayLine today={today} />
        </div>
      </div>
    </div>
  );
}

// Today's lowest / highest 10-second TPS (2026-10-21), from the `today`
// field of /transaction-tps - same data as the embeddable widget's line, see
// CallbackStatsStore::getTpsDayRange(). Times are trimmed to HH:MM. Renders
// an &nbsp; line until the first 10s bucket of the day completes, so the
// card height doesn't jump.
function formatTpsTime(v) {
  return typeof v === 'string' && v.length >= 5 ? ` at ${v.slice(0, 5)}` : '';
}

function TpsTodayLine({ today }) {
  if (!today || today.low === undefined || today.high === undefined) {
    return <div className="text-muted small">&nbsp;</div>;
  }
  const since = typeof today.since === 'string' ? ` since ${today.since.slice(0, 5)}` : '';
  return (
    <div className="text-muted small" title={`Lowest and highest 10-second TPS today${since}`}>
      <span className="text-nowrap">Low {Number(today.low).toFixed(2)}{formatTpsTime(today.low_at)}</span>
      {' · '}
      <span className="text-nowrap">High {Number(today.high).toFixed(2)}{formatTpsTime(today.high_at)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab - on-demand (not polled) date-range/filtered search over
// TransactionHistoryStore's DynamoDB table, via P2MDashboard::
// transactionHistory(). Every transaction_type and outcome, retained
// forever - unlike the live summary above this can find a transaction from
// months ago, long after its Redis record (and the recent-N feed) expired.
// ---------------------------------------------------------------------------
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// The two History bounds are <input type="datetime-local">, whose value is
// 'YYYY-MM-DDTHH:MM'. The API keeps date and time as separate params
// (from/from_time), so these two helpers are the only place the two
// representations meet.
const START_OF_DAY = '00:00';
const END_OF_DAY = '23:59';

const atTime = (dateIso, time) => `${dateIso}T${time}`;

// 'YYYY-MM-DDTHH:MM' is exactly 16 characters. Anything longer is a seconds
// or milliseconds component, and keeping it would make the input fail native
// constraint validation: with no `step` attribute a datetime-local's step is
// 60s, so a value carrying seconds is a step mismatch, the form refuses to
// submit, and Search appears to do nothing. Normalising on the way into state
// keeps the control always valid.
const trimToMinutes = (value) => (value ? value.slice(0, 16) : '');

// Returns [date, time]. A datetime-local is either wholly empty or complete,
// so a missing time here means the whole field is blank, not "unspecified
// time" - which is exactly why folding date+time into one control is safe now
// and was not with two separate inputs.
const splitLocal = (value) => {
  if (!value) return [undefined, undefined];
  const [date, time] = value.split('T');
  if (!date) return [undefined, undefined];
  // Chrome emits seconds only when step is set; we do not set it, but trim
  // defensively so 'HH:MM:SS' never reaches the API as a surprise.
  return [date, time ? time.slice(0, 5) : undefined];
};

function HistoryTab({ preset }) {
  const merchantNames = useMerchantNames();

  // Time-of-day narrowing (2026-08-27), as a single datetime-local per bound
  // so the browser's own calendar popover carries the clock columns instead
  // of a second box sitting beside it.
  //
  // The defaults carry the edges explicitly - start-of-day on FROM,
  // end-of-day on TO - rather than leaving time blank. A datetime-local has
  // no "date set, time unset" state to lean on, so the asymmetry that made
  // two separate inputs necessary is spelled out here once instead. 23:59 on
  // the TO edge is expanded to 23:59:59.999999 server-side, so the last
  // minute of the day is included.
  const [fromInput, setFromInput] = useState(atTime(daysAgoIso(7), START_OF_DAY));
  const [toInput, setToInput] = useState(atTime(todayIso(), END_OF_DAY));

  const [transactionType, setTransactionType] = useState('');
  const [status, setStatus] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [txnStatus, setTxnStatus] = useState(TXN_STATUS_ANY);

  // Free-text filter across every column (2026-08-26). Applied in the
  // browser over rows already loaded, NOT sent to the API - DynamoDB has no
  // full-text capability here, and the alternative (scanning a day's
  // partition server-side per keystroke) would be far more expensive than
  // filtering an array. The consequence is honest and surfaced in the UI:
  // it searches what has been loaded, so "Load more" widens what it can
  // find.
  const [query, setQuery] = useState('');

  // '1' rejected / '0' not rejected / undefined either - see
  // TransactionHistoryStore::getByDate(). Sent as a string because '0' is a
  // real value here, not an absence.
  const rejectedParam = txnStatus === TXN_STATUS_REJECTED
    ? '1'
    : (txnStatus === TXN_STATUS_OK ? '0' : undefined);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [page, setPage] = useState(1);

  // { total, exact } for the current search, or null while it is in flight
  // or if it failed. Never blocks the rows - see runSearch().
  const [total, setTotal] = useState(null);

  const runSearch = useCallback(async (cursor, overrides = {}) => {
    setLoading(true);
    setError(null);
    // `key in overrides` rather than `overrides.key ?? state`: the preset path
    // passes some keys as an explicit undefined to mean "cleared", and `??`
    // would read them as "absent" and fall back to the stale state value that
    // has not been re-rendered yet. Presence of the key is the signal.
    const pick = (key, fallback) => (key in overrides ? overrides[key] : fallback);

    const [stateFrom, stateFromTime] = splitLocal(fromInput);
    const [stateTo, stateToTime] = splitLocal(toInput);

    // Exactly the params that define the SEARCH. The count endpoint takes
    // the same object minus paging, so the two can never describe different
    // queries.
    const search = {
      from: pick('from', stateFrom),
      to: pick('to', stateTo),
      fromTime: pick('fromTime', stateFromTime),
      toTime: pick('toTime', stateToTime),
      transactionType: pick('transactionType', transactionType || undefined),
      status: pick('status', status || undefined),
      merchantId: pick('merchantId', merchantId.trim() || undefined),
      rejected: pick('rejected', rejectedParam),
    };

    // Only on a fresh search, not on a "Next" that pages the same one: the
    // total is a property of the search and does not change as pages load.
    if (!cursor) {
      setTotal(null);
      // Fire-and-forget, deliberately NOT awaited and NOT inside the try
      // below. Counting is the expensive half (DynamoDB has no cheap
      // filtered COUNT) and this must never delay the rows or turn a
      // count failure into a failed search - worst case the line just
      // says "25 records loaded" as it did before.
      getTransactionHistoryCount(search)
        .then((c) => setTotal(c))
        .catch(() => setTotal(null));
    }

    try {
      const data = await getTransactionHistory({
        ...search,
        limit: 25,
        cursor: cursor || undefined,
      });

      setResults((prev) => (cursor && prev
        ? { ...data, items: [...prev.items, ...data.items] }
        : data));
      setNextCursor(data.next_cursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load transaction history.');
    } finally {
      setLoading(false);
    }
  }, [fromInput, toInput, transactionType, status, merchantId, rejectedParam]);

  // A "View all today" click from Live summary arrives as a preset: apply it
  // to the form AND run the search immediately, so the tab lands on results
  // rather than on a form the user has to submit again.
  //
  // runSearch() is deliberately called with explicit overrides rather than
  // relying on the state set just above - those setState calls have not been
  // applied yet at this point in the same tick, so runSearch would otherwise
  // read the PREVIOUS filters and fetch the wrong rows.
  useEffect(() => {
    if (!preset) return;

    // A preset means "this whole day", so it resets both bounds to the full
    // span rather than inheriting whatever window the user had set.
    const presetFrom = preset.from ?? todayIso();
    const presetTo = preset.to ?? todayIso();

    setFromInput(atTime(presetFrom, START_OF_DAY));
    setToInput(atTime(presetTo, END_OF_DAY));
    setStatus(preset.status ?? '');
    setTransactionType(preset.transactionType ?? '');
    setTxnStatus(preset.rejected ? TXN_STATUS_REJECTED : TXN_STATUS_ANY);
    setMerchantId('');
    setQuery('');
    setResults(null);
    setNextCursor(null);
    setPage(1);

    runSearch(null, {
      from: presetFrom,
      to: presetTo,
      fromTime: START_OF_DAY,
      toTime: END_OF_DAY,
      status: preset.status ?? undefined,
      transactionType: preset.transactionType ?? undefined,
      merchantId: undefined,
      rejected: preset.rejected ? '1' : undefined,
    });
    // runSearch is intentionally omitted: it is recreated whenever a filter
    // changes, and including it would re-run the preset search on every
    // subsequent edit to the form. `preset.token` changes on each click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.token]);

  // datetime-local strings sort lexicographically in chronological order, so
  // this comparison needs no parsing.
  const rangeInverted = Boolean(fromInput && toInput && fromInput > toInput);

  const handleSearch = (e) => {
    e.preventDefault();
    // Caught here rather than left to the server: a backwards window is a
    // real range that simply contains nothing, so the API would answer "0
    // transactions" and the user would read that as "no data" instead of
    // "your bounds are the wrong way round".
    if (rangeInverted) {
      setResults(null);
      setNextCursor(null);
      setError('The "From" datetime is after the "To" datetime — no rows can fall in that range.');
      return;
    }
    setResults(null);
    setNextCursor(null);
    setPage(1);
    runSearch();
  };

  // Match across every column the table renders, including the resolved
  // merchant NAME rather than only the raw ID - searching for "ArenaPlus"
  // has to work when that is what the row displays.
  const allItems = results?.items || [];
  const needle = query.trim().toLowerCase();
  const filteredItems = needle
    ? allItems.filter((row) => [
        row.transaction_type,
        row.status,
        row.rejection_code,
        row.instruction_id,
        row.merchant_order_id,
        row.merchant_id,
        merchantLabel(merchantNames, row.merchant_id),
        row.amount,
        row.created_at,
      ].some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(needle)))
    : allItems;

  const totalPages = Math.max(Math.ceil(filteredItems.length / HISTORY_PAGE_SIZE), 1);
  const safePage = Math.min(page, totalPages);
  const pageItems = filteredItems.slice((safePage - 1) * HISTORY_PAGE_SIZE, safePage * HISTORY_PAGE_SIZE);

  // Whether the server still has rows beyond what has been fetched. Drives
  // both the "+" on the page count and whether Next can keep going.
  const canLoadMore = Boolean(nextCursor);

  // The "out of N" half of the count line, or null while the count is in
  // flight / failed / not applicable.
  //
  // An inexact total is a LOWER bound (the server's query budget ran out),
  // so it is rendered "1,234+" - and floored at the number of rows already
  // on screen, because a budget that stopped early can legitimately report
  // fewer than have been loaded, and "25 loaded out of 10+" reads as a bug.
  const totalLabel = total
    ? (total.exact
        ? formatCount(total.total)
        : `${formatCount(Math.max(total.total, allItems.length))}+`)
    : null;

  // Next past the last loaded page pulls the next slice first, then
  // advances onto it.
  const handleNextPage = async () => {
    if (safePage < totalPages) {
      setPage(safePage + 1);
      return;
    }
    if (canLoadMore) {
      await runSearch(nextCursor);
      setPage(safePage + 1);
    }
  };

  return (
    <div>
      <p className="text-muted small mb-3">
        Searches every transaction ever recorded, not just today&rsquo;s QP2M/ICRT successes.
        Date range is capped at 31 days per search.
      </p>

      <form className="row row-cols-lg-auto g-2 align-items-end mb-3" onSubmit={handleSearch}>
        {/* One datetime-local per bound: the browser's own calendar popover
            carries the hour/minute columns, so the time lives inside the
            date picker instead of in a second box beside it. No `step`, so
            the popover stays at minute granularity - the API drops seconds
            anyway, and showing a seconds column we then ignore would be a
            lie.

            Deliberately no `min`/`max` cross-bound clamp. Those make an
            inverted range fail native constraint validation, which blocks
            form submission entirely - Search silently does nothing and the
            only feedback is a terse browser bubble. handleSearch() checks the
            order itself and says so in the page instead. */}
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="history-from">From</label>
          <input
            id="history-from"
            type="datetime-local"
            className={`form-control form-control-sm${rangeInverted ? ' is-invalid' : ''}`}
            style={{ width: '13.5rem' }}
            value={fromInput}
            onChange={(e) => setFromInput(trimToMinutes(e.target.value))}
          />
        </div>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="history-to">To</label>
          <input
            id="history-to"
            type="datetime-local"
            className={`form-control form-control-sm${rangeInverted ? ' is-invalid' : ''}`}
            style={{ width: '13.5rem' }}
            value={toInput}
            onChange={(e) => setToInput(trimToMinutes(e.target.value))}
          />
        </div>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="history-type">Type</label>
          <select
            id="history-type"
            className="form-select form-select-sm"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
          >
            <option value="">Any</option>
            <option value={DEPOSIT_TYPE}>Deposit (QP2M)</option>
            <option value={WITHDRAWAL_TYPE}>Withdrawal (ICRT)</option>
          </select>
        </div>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="history-status">Callback delivery status</label>
          <select
            id="history-status"
            className="form-select form-select-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Any</option>
            <option value="success">Success</option>
            <option value="failed">Failed (retrying)</option>
            <option value="exhausted">Exhausted</option>
          </select>
        </div>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="history-txn-status">
            Actual transaction status
          </label>
          <select
            id="history-txn-status"
            className="form-select form-select-sm"
            value={txnStatus}
            onChange={(e) => setTxnStatus(e.target.value)}
          >
            <option value={TXN_STATUS_ANY}>Any</option>
            <option value={TXN_STATUS_OK}>Successful</option>
            <option value={TXN_STATUS_REJECTED}>Rejected</option>
          </select>
        </div>
        <div className="col-12">
          <label className="form-label small text-muted mb-1" htmlFor="history-merchant">Merchant ID</label>
          <input
            id="history-merchant"
            type="text"
            className="form-control form-control-sm"
            placeholder="Optional"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
          />
        </div>
        <div className="col-12">
          <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>
            {loading && !results ? 'Searching…' : 'Search'}
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {results && (
        <>
          <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-2">
            {/* "loaded out of N" once the count lands (2026-08-27, user
                request). Until then, or if the count failed or was capped
                short, the wording stays honest about what the number is:
                "loaded so far" while a cursor remains, "found" once
                everything really has been fetched.

                `total.exact === false` means the server's query budget ran
                out and its number is a LOWER bound, so it renders "1,234+".
                Never silently print a floor as if it were the total. */}
            <p className="text-muted small mb-0">
              {results.from}{results.from_time ? ` ${results.from_time}` : ''}
              {' '}to {results.to}{results.to_time ? ` ${results.to_time}` : ''} ·{' '}
              {needle && (
                <>
                  <strong>{formatCount(filteredItems.length)}</strong>
                  {' '}matching &middot;{' '}
                </>
              )}
              <strong>{formatCount(allItems.length)}</strong>
              {' '}record{allItems.length === 1 ? '' : 's'}
              {totalLabel
                ? ` loaded out of ${totalLabel}`
                : (canLoadMore ? ' loaded so far' : ' found')}
              {' '}· Last refreshed: {results.generated_at}
            </p>
            <div style={{ minWidth: '16rem' }}>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Filter loaded rows (any column)…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                aria-label="Filter loaded rows across all columns"
              />
            </div>
          </div>

          {/* Kept even though the count line above also says "so far":
              this one explains WHY a search can miss something, which the
              count alone does not. Only while more remains. */}
          {needle && canLoadMore && (
            <p className="text-muted small mb-2">
              Filtering the rows loaded so far &mdash; press Next to pull more from the server.
            </p>
          )}

          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Type</th>
                    {/* CALLBACK DELIVERY, not the transaction's own
                        outcome - the Rejection code column beside it is
                        that. Named in full because "Status" alone read as
                        the transaction's result, which is exactly the
                        confusion this page had to correct. */}
                    <th>Callback delivery</th>
                    <th>Rejection code</th>
                    <th>Instruction ID</th>
                    <th>Merchant order ID</th>
                    <th>Merchant</th>
                    <th className="text-end">Amount</th>
                    <th>Created at</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-muted text-center py-3">
                        {needle ? 'No loaded rows match that filter.' : 'No transactions match this search.'}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((row) => (
                    <tr key={row.merchant_order_id}>
                      <td>{typeBadge(row.transaction_type)}</td>
                      <td>{statusBadge(row.status)}</td>
                      {/* Dash for rows written before rejection_code was
                          promoted to a top-level attribute (2026-08-26), and
                          for the ordinary case of a transaction that was
                          never rejected. */}
                      <td>
                        {row.rejection_code
                          ? <span className="badge text-bg-danger">{row.rejection_code}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      {/* Blank for anything recorded before instruction_id
                          was promoted to a top-level DynamoDB attribute
                          (2026-08-24) - those items are already written and
                          are never backfilled. */}
                      <td className="text-nowrap">{row.instruction_id || '—'}</td>
                      <td className="text-break">{row.merchant_order_id}</td>
                      <td className="text-break" title={row.merchant_id || ''}>
                        {merchantLabel(merchantNames, row.merchant_id)}
                      </td>
                      <td className="text-end">{formatAmount(row.amount)}</td>
                      <td>{row.created_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ONE paging control, not two (2026-08-26). This first shipped as
              page numbers over loaded rows PLUS a separate "Load more from
              server" button, which made the user ask what the pagination
              was even for - a fair question, since two controls for "show
              me more" is one too many.
              
              Now Next fetches transparently: if you are on the last loaded
              page and the server still has a cursor, it pulls the next
              slice and then advances. The distinction between "already in
              the browser" and "still on the server" stops being the
              operator's problem.

              It still cannot be true server-side pagination. The by-date
              GSI pages by opaque cursor, forward only, so page 7 is only
              reachable by walking 1-6 - which is exactly what Next now
              does, one step at a time, instead of pretending to random
              access. */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={safePage <= 1 || loading}
              >
                Previous
              </button>
              <span className="text-muted small">
                Page {safePage} of {totalPages}{canLoadMore ? '+' : ''}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleNextPage}
                disabled={loading || (safePage >= totalPages && !canLoadMore)}
              >
                {loading && safePage >= totalPages ? 'Loading…' : 'Next'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
